import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

interface UnsavedChangesRegistry {
  [key: string]: () => boolean; // page identifier -> hasUnsavedChanges function
}

interface NavigationContextType {
  interceptedNavigate: NavigateFunction;
  interceptAction: (action: () => void) => void;
  registerUnsavedChanges: (pageId: string, hasUnsavedChanges: () => boolean) => void;
  unregisterUnsavedChanges: (pageId: string) => void;
  showUnsavedDialog: boolean;
  setShowUnsavedDialog: (show: boolean) => void;
  pendingNavigation: (() => void) | null;
  setPendingNavigation: (nav: (() => void) | null) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [unsavedChangesRegistry, setUnsavedChangesRegistry] = useState<UnsavedChangesRegistry>({});
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const navigateRef = useRef(navigate);

  React.useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const registerUnsavedChanges = useCallback((pageId: string, hasUnsavedChanges: () => boolean) => {
    setUnsavedChangesRegistry(prev => ({
      ...prev,
      [pageId]: hasUnsavedChanges,
    }));
  }, []);

  const unregisterUnsavedChanges = useCallback((pageId: string) => {
    setUnsavedChangesRegistry(prev => {
      const next = { ...prev };
      delete next[pageId];
      return next;
    });
  }, []);

  const hasAnyUnsavedChanges = useCallback(() => {
    return Object.values(unsavedChangesRegistry).some(checker => checker());
  }, [unsavedChangesRegistry]);

  const interceptedNavigate = useCallback<NavigateFunction>(
    (to: string | number, options?: any) => {
      if (hasAnyUnsavedChanges()) {
        setShowUnsavedDialog(true);
        setPendingNavigation(() => () => {
          if (typeof to === 'number') {
            navigateRef.current(to);
          } else {
            navigateRef.current(to, options);
          }
        });
        return;
      }

      if (typeof to === 'number') {
        navigateRef.current(to);
      } else {
        navigateRef.current(to, options);
      }
    },
    [hasAnyUnsavedChanges, unsavedChangesRegistry]
  );

  const interceptAction = useCallback(
    (action: () => void) => {
      if (hasAnyUnsavedChanges()) {
        setShowUnsavedDialog(true);
        setPendingNavigation(() => action);
        return;
      }
      action();
    },
    [hasAnyUnsavedChanges]
  );

  return (
    <NavigationContext.Provider
      value={{
        interceptedNavigate,
        interceptAction,
        registerUnsavedChanges,
        unregisterUnsavedChanges,
        showUnsavedDialog,
        setShowUnsavedDialog,
        pendingNavigation,
        setPendingNavigation,
      }}
    >
      {children}
      
      <AlertDialog open={showUnsavedDialog} onOpenChange={(open) => {
        if (!open) {
          setShowUnsavedDialog(false);
          setPendingNavigation(null);
        }
      }}>
        <AlertDialogContent className="max-w-md p-0 overflow-hidden rounded-xl border border-gray-200 shadow-2xl">
          <div className="h-1 w-full bg-gradient-to-r from-amber-400 via-orange-400 to-red-400" />

          <div className="p-6">
            <AlertDialogHeader className="space-y-3 text-left">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 ring-4 ring-amber-50">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <AlertDialogTitle className="text-base font-semibold text-gray-900 leading-tight">
                    You have unsaved changes
                  </AlertDialogTitle>
                  <AlertDialogDescription className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                    Your edits haven't been saved yet. If you leave now, they'll be permanently lost.
                  </AlertDialogDescription>
                </div>
              </div>
            </AlertDialogHeader>

            <AlertDialogFooter className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <AlertDialogCancel
                onClick={() => {
                  setShowUnsavedDialog(false);
                  setPendingNavigation(null);
                }}
                className="mt-0 h-9 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-gray-200 focus-visible:ring-offset-0"
              >
                Keep editing
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setShowUnsavedDialog(false);
                  if (pendingNavigation) {
                    pendingNavigation();
                    setPendingNavigation(null);
                  }
                }}
                className="h-9 rounded-md bg-red-600 px-4 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-200 focus-visible:ring-offset-0"
              >
                Discard changes
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const useOptionalNavigation = () => useContext(NavigationContext);

